import type { PaletteEntry } from './entries';

/**
 * Turns remembered ids back into entries, against what exists *now*.
 *
 * The list is persisted to the device and the application is not: a workspace can be
 * deleted, a workflow renamed, a task closed between one session and the next. An id that
 * no longer resolves is **dropped**, never rendered as a row that goes nowhere — the same
 * instinct as the navigation gate, where a door nobody can open is not drawn.
 *
 * Order comes from the remembered list, not from the entries, because the whole point is
 * recency.
 */
export function resolveRecents(
  ids: string[],
  entries: PaletteEntry[],
): PaletteEntry[] {
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  return ids
    .map((id) => byId.get(id))
    .filter((entry): entry is PaletteEntry => entry !== undefined)
    .map((entry) => ({ ...entry, group: 'recent' as const }));
}

/**
 * What the palette shows before anything is typed.
 *
 * Recents **and** what the palette can do — not one or the other. FR-021's own reading of
 * "something useful" is "recent and likely destinations", and this used to return early
 * the moment a single recent existed, so the actions disappeared permanently after the
 * first use. A palette that teaches only the reader who has never opened it teaches
 * nobody: the first use is the one time you are least able to tell which of the offered
 * rows mattered, and every use afterwards showed a history of three things and no hint
 * that the other twenty existed.
 *
 * Recents come first, because when they are what you wanted they are what you wanted
 * immediately. They are capped by the store (`RECENTS_KEPT`), not here — a cap in two
 * places is a cap that will eventually disagree with itself.
 *
 * Anything already listed as recent is dropped from the sections below it. The same row
 * twice is not twice as discoverable; it just costs a line that a not-yet-discovered one
 * could have used — which is why the page slice is taken *after* that filter rather than
 * before, so the count of pages on offer does not quietly shrink as they are used.
 */
export function openingEntries(
  ids: string[],
  entries: PaletteEntry[],
  fallbackCount = 5,
): PaletteEntry[] {
  const recents = resolveRecents(ids, entries);
  const alreadyShown = new Set(recents.map((entry) => entry.id));
  const unseen = entries.filter((entry) => !alreadyShown.has(entry.id));

  return [
    ...recents,
    ...unseen.filter((entry) => entry.kind === 'action'),
    ...unseen.filter((entry) => entry.kind === 'page').slice(0, fallbackCount),
  ];
}
